import React from "react";
import { Button, Select, MenuItem, Checkbox, ListItemText, FormControl, Box, CircularProgress } from "@mui/material";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import { styled as muiStyled } from "@mui/material";
import { withTheme } from "@mui/styles";
import { M3Body, M3BodyMedium } from "./TextStyles";

import KeyboardBackspaceIcon from "@mui/icons-material/KeyboardBackspace";
import theme from "../../utils/theme";
import { useState } from "react";
import { PaddingBox } from "./Scaffolds";
import { useResponsive } from "../../hooks/useResponsive";

const deactiveDropdownStyles = `
  .da-dropdown-row:hover { background: ${theme.palette.onSurfaceTransparent.main} !important; }
  @keyframes daDropdownIn {
    from { opacity: 0; transform: translateY(-4px) scale(0.98); }
    to { opacity: 1; transform: translateY(0) scale(1); }
  }
`;

export function DeactiveAndActiveButton({ onClick, values, text, isDisabled, isLoading }) {
  const [open, setOpen] = useState(false);
  const dropdownRef = React.useRef(null);
  const isDeactive = text === 'Deactivate';

  React.useEffect(() => {
    function handleClickOutside(e) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (id) => {
    setOpen(false);
    onClick(id);
  };

  if (values.length === 0) return null;
  return (
    <div ref={dropdownRef} style={{ position: 'relative', marginRight: 16 }}>
      <style>{deactiveDropdownStyles}</style>
      <FullSizedButton
        size='small'
        variant='contained'
        disabled={isDisabled}
        isLoading={isLoading}
        type={isDeactive ? 'tertiary' : 'success'}
        text={text}
        onClick={() => { if (!isDisabled) setOpen(!open); }}
        endIcon={<ArrowDropDownIcon />}
      />
      {open && (
        <div style={{
          position: 'absolute',
          top: 'calc(100% + 4px)',
          left: 0,
          minWidth: 220,
          background: theme.palette.surface.main,
          borderRadius: 10,
          border: `1px solid ${theme.palette.outLineVariant.main}`,
          boxShadow: '0 8px 30px rgba(113, 15, 234, 0.1), 0 2px 8px rgba(0,0,0,0.04)',
          overflow: 'hidden',
          animation: 'daDropdownIn 0.18s ease-out',
          zIndex: 100,
        }}>
          <div style={{
            padding: '8px 12px 4px',
          }}>
            <span style={{
              fontSize: 11,
              fontWeight: 600,
              color: theme.palette.outline.main,
              letterSpacing: '0.06em',
              textTransform: 'uppercase',
              fontFamily: 'Inter, sans-serif',
            }}>
              {text}
            </span>
          </div>
          <div style={{ maxHeight: 240, overflowY: 'auto', padding: '4px 6px 8px' }}>
            {values.map((value) => (
              <div
                key={value.organisation_id}
                className='da-dropdown-row'
                onClick={() => handleSelect(value.organisation_id)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  padding: '8px 10px',
                  borderRadius: 7,
                  cursor: 'pointer',
                  transition: 'background 0.1s ease',
                  userSelect: 'none',
                  fontSize: 13,
                  fontFamily: 'Inter, sans-serif',
                  color: theme.palette.onSurfaceVariant.main,
                  fontWeight: 400,
                }}
              >
                {value.organisation_name}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
export function PrimaryButton({ onClick, text, isDisabled = false, isLoading = false }) {
  return (
    <CreateButton disabled={isDisabled} variant="contained" type="submit" onClick={onClick} color="info">
      {isLoading ? <CircularProgress size={24} sx={{ color: "white" }}></CircularProgress> : <M3Body>{text}</M3Body>}
    </CreateButton>
  );
}

export function SuccessButton({ onClick, text, isDisabled }) {
  return (
    <SuccessButtonStyled onClick={onClick} variant="contained" disabled={isDisabled}>
      {text}
    </SuccessButtonStyled>
  );
}

export function RemoveButton({ onClick, text, isDisabled }) {
  return (
    <EndButton onClick={onClick} variant="contained" disabled={isDisabled}>
      <M3Body>{text}</M3Body>
    </EndButton>
  );
}
export function CloseButton({ onClick, text, isDisabled = false, isLoading = false }) {
  return (
    <CancelButton onClick={onClick} variant="contained" disabled={isDisabled}>
      {isLoading ? <CircularProgress size={24} sx={{ color: "inherit" }}></CircularProgress> : <M3Body>{text}</M3Body>}
    </CancelButton>
  );
}

const CancelButton = muiStyled(withTheme(Button))`
    text-transform: none;
    padding: 8px 16px;
      border-radius: 28px;
    background-color: ${(props) => props.theme.palette.secondary.main} !important;
    color: ${(props) => props.theme.palette.secondary.onSecondary} !important;
    &:hover {
      opacity: 0.8;
    }
    `;

const EndButton = muiStyled(withTheme(Button))`
    text-transform: none;
    border-radius: 28px;
    padding: 8px 16px;
    background-color: ${(props) => props.theme.palette.tertiaryContainer.main} !important;
    color: ${(props) => props.theme.palette.tertiaryContainer.onTertiary} !important;
    &:hover {
      opacity: 0.8;
    }
    `;
const CreateButton = muiStyled(withTheme(Button))`
    text-transform: none;
    border-radius: 28px;
    padding: 8px 16px;
    &:hover {
      opacity: 0.8;
    }
`;
const SuccessButtonStyled = muiStyled(withTheme(Button))`
  text-transform: none;
  border-radius: 28px;
  padding: 8px 16px;
  background-color: #4caf50;
  color: white;
  &:hover {
    background-color: darkgreen;
    opacity: 0.8;
  }
`;

const SelectCustom = muiStyled(withTheme(Select))`
    height: 33px;
    font-family: Inter, sans-serif;
    font-size: 13px;
    font-style: normal;
    font-weight: 400;
    line-height: 20px;
    letter-spacing: 0.1px;
    border-radius: 8px;
    & .MuiOutlinedInput-notchedOutline {
        border-color: #C4C7C7;
        border-width: 0.5px;
    }
    &:hover .MuiOutlinedInput-notchedOutline {
        border-color: #747878;
    }
    &.Mui-disabled .MuiOutlinedInput-notchedOutline {
        border-color: rgba(0, 0, 0, 0.2);
    }
    &.MuiInputBase-root .Mui-disabled {
        -webkit-text-fill-color: rgba(0, 0, 0, 0.8);
        color: rgba(0, 0, 0, 0.8);
    }
    & .MuiSelect-select {
        padding: 6px 12px;
        font-size: 13px;
    }
`;

/** Matches SelectCustom / FilterButton menu row typography */
const filterMenuItemPrimarySx = {
  fontFamily: 'Inter, sans-serif',
  fontSize: '13px',
  fontWeight: 400,
  lineHeight: "20px",
  letterSpacing: "0.1px",
};

export function FilterButton({ currentValue, onChange, options, placeholder, onClick, isDisabled = false, fullWidth = false, customMargin }) {
  return (
    <SelectCustom
      value={currentValue}
      displayEmpty
      inputProps={{ "aria-label": "Without label" }}
      onChange={onChange}
      onClick={onClick}
      autoWidth
      style={{ borderRadius: "8px", margin: customMargin || "0 0 0 16px" }}
      disabled={isDisabled}
      placeholder={placeholder}
      fullWidth={fullWidth}
      sx={{
        borderRadius: 2,
      }}
    >
      <MenuItem disabled value={""}>
        {placeholder}
      </MenuItem>
      {options.map((option) => (
        <MenuItem key={option.id} value={option.id} dense>
          {option.name}
        </MenuItem>
      ))}
    </SelectCustom>
  );
}

export function FilterButtonMultiple({
  currentValue,
  onChange,
  options,
  placeholder,
  onClick,
  isDisabled = false,
  fullWidth = false,
  label = "location",
  customMargin,
}) {
  const renderValue = (selected) => {
    if (selected.length === 0) return placeholder;
    if (!label) return `${selected.length} selected`;
    return `${selected.length} ${label}${selected.length > 1 ? "s" : ""} selected`;
  };
  return (
    <SelectCustom
      value={currentValue}
      displayEmpty
      inputProps={{ "aria-label": "Without label" }}
      onChange={onChange}
      onClick={onClick}
      renderValue={renderValue}
      autoWidth
      multiple
      fullWidth={fullWidth}
      style={{
        borderRadius: "8px",
        margin: customMargin || "0 0 0 16px",
        backgroundColor: isDisabled ? "rgba(0, 0, 0, 0.12)" : "inherit",
      }}
      disabled={isDisabled}
      placeholder={placeholder}
      sx={{
        borderRadius: 2,
      }}
    >
      {options.map((option) => (
        <MenuItem key={option.id} value={option.id} dense>
          <Checkbox size="small" sx={{ py: 0 }} checked={currentValue.indexOf(option.id) > -1} />
          <ListItemText primary={option.name} primaryTypographyProps={{ sx: filterMenuItemPrimarySx }} />
        </MenuItem>
      ))}
    </SelectCustom>
  );
}

export function FilterEmpty({ currentValue, placeholder, onClick, isDisabled = false }) {
  return (
    <SelectCustom
      value={currentValue}
      displayEmpty
      inputProps={{ "aria-label": "Without label" }}
      onClick={onClick}
      autoWidth
      style={{ borderRadius: "8px" }}
      disabled={isDisabled}
      placeholder={placeholder}
      sx={{
        borderRadius: 2,
      }}
    >
      <MenuItem disabled value={""}>
        {placeholder}
      </MenuItem>
    </SelectCustom>
  );
}

export const BackButton = ({ title, onClick }) => {
  return (
    <Button
      startIcon={<KeyboardBackspaceIcon />}
      onClick={onClick}
      sx={{
        textTransform: "none",
        fontSize: "14px",
        fontWeight: 500,
        color: theme.palette.info.main,
      }}
    >
      {title}
    </Button>
  );
};

export function DropDownButton({ currentValue, onChange, options, placeholder, onClick, isDisabled = false }) {
  const hasValue = currentValue !== '' && currentValue !== null && currentValue !== undefined;
  return (
    <FormControl fullWidth variant={isDisabled ? 'filled' : 'outlined'}>
      <Select
        value={currentValue ?? ''}
        onChange={onChange}
        onClick={onClick}
        disabled={isDisabled}
        displayEmpty
        renderValue={(selected) => {
          if (!hasValue) {
            return (
              <span style={{ color: theme.palette.gray[400] }}>
                {placeholder || 'Select…'}
              </span>
            );
          }
          // Find the matching MenuItem child to display its label.
          const items = React.Children.toArray(options);
          const match = items.find((c) => c?.props?.value === selected);
          return match?.props?.children ?? selected;
        }}
        sx={{
          backgroundColor: '#FFFFFF',
          borderRadius: '8px',
          fontFamily: 'Inter, sans-serif',
          fontSize: '13px',
          color: theme.palette.gray[800],
          '& .MuiSelect-select': {
            padding: '9px 12px',
            paddingRight: '32px',
          },
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: theme.palette.gray[200],
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: theme.palette.gray[300],
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: theme.palette.primary?.outline || '#D4BBFB',
            borderWidth: '1px',
            boxShadow: '0 0 0 3px rgba(113,15,234,.08)',
          },
        }}
      >
        {options}
      </Select>
    </FormControl>
  );
}

export function ApprovalCheckbox({ isChecked = false, onChange, content }) {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
      }}
    >
      <Checkbox checked={isChecked} onChange={onChange} sx={{ color: isChecked ? theme.palette.primary.main : "" }} />
      <M3BodyMedium>{content}</M3BodyMedium>
    </Box>
  );
}
/**
 * A customizable full-width button component with different color themes
 *
 * @component
 * @param {Object} props - Component props
 * @param {string} [props.text] - Button text content
 * @param {Function} props.onClick - Click event handler
 * @param {('primary'|'secondary'|'primaryContainer'|'tertiary')} props.type - Button style theme
 * @param {React.ReactNode} [props.icon] - Icon to display inside the button
 * @param {boolean} [props.disabled=false] - Whether the button is disabled
 * @param {boolean} [props.dense=false] - If true, button has auto width instead of full width
 *
 * @example
 * // Primary button with text
 * <FullSizedButton
 *   text="Save Changes"
 *   onClick={handleSave}
 *   type="primary"
 * />
 *
 * @example
 * // Icon-only tertiary button
 * <FullSizedButton
 *   icon={<DeleteIcon />}
 *   onClick={handleDelete}
 *   type="tertiary"
 *   dense={true}
 * />
 *
 * @returns {React.ReactElement} Rendered button component
 */
export function FullSizedButton({ text, onClick, type, icon, disabled, dense, isLoading, fullWidth = true, endIcon, small = false, mobileIconOnly = false }) {
  const { isMobile } = useResponsive();
  const showText = mobileIconOnly && isMobile ? null : text;
  // Color themes mirror the HTML mock: solid purple/emerald/rose for actions,
  // white-with-border for cancel/secondary. Every type variant from the legacy
  // API is preserved.
  const colorTheme = {
    primary: {
      backgroundColor: theme.palette.primary.main,
      color: '#FFFFFF',
      border: '1px solid transparent',
      '&:hover': {
        backgroundColor: '#5A0FBB',
      },
    },
    secondary: {
      backgroundColor: '#FFFFFF',
      color: theme.palette.gray[600],
      border: `1px solid ${theme.palette.gray[200]}`,
      '&:hover': {
        backgroundColor: theme.palette.gray[50],
        borderColor: theme.palette.gray[300],
      },
    },
    primaryContainer: {
      backgroundColor: theme.palette.primary?.faint || '#F5F0FF',
      color: theme.palette.primary.main,
      border: `1px solid ${theme.palette.primary?.outline || '#EDE5FD'}`,
      '&:hover': {
        backgroundColor: '#EDE5FD',
      },
    },
    success: {
      backgroundColor: theme.palette.emerald.main,
      color: '#FFFFFF',
      border: '1px solid transparent',
      '&:hover': {
        backgroundColor: theme.palette.emerald.onContainer,
      },
    },
    tertiary: {
      backgroundColor: theme.palette.rose.main,
      color: '#FFFFFF',
      border: '1px solid transparent',
      '&:hover': {
        backgroundColor: theme.palette.rose.onSurface,
      },
    },
    outline: {
      backgroundColor: 'transparent',
      color: theme.palette.onSurfaceVariant?.main || theme.palette.gray[700],
      border: `1px solid ${theme.palette.gray[200]}`,
      boxShadow: 'none',
      '&:hover': {
        backgroundColor: theme.palette.gray[50],
        borderColor: theme.palette.gray[300],
        boxShadow: 'none',
      },
    },
  };
  const variantStyles = colorTheme[type] || colorTheme.primary;
  const iconComponent = icon ? (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', color: 'inherit', '& svg': { fontSize: small ? 14 : 15 } }}>
      {icon}
    </Box>
  ) : null;
  return (
    <Button
      variant='contained'
      disableElevation
      disableRipple={true}
      startIcon={showText ? iconComponent : null}
      endIcon={endIcon}
      onClick={onClick}
      type='submit'
      disabled={isLoading || disabled}
      sx={{
        borderRadius: '8px',
        marginY: dense ? 0 : 0.5,
        textTransform: 'none',
        height: small ? '28px' : '36px',
        minHeight: small ? '28px' : undefined,
        padding: mobileIconOnly && isMobile ? '8px' : small ? '4px 12px' : '8px 16px',
        minWidth: mobileIconOnly && isMobile ? '36px' : undefined,
        fontSize: small ? '12px' : '13px',
        fontWeight: 600,
        fontFamily: 'Inter, sans-serif',
        letterSpacing: 0,
        lineHeight: 1.2,
        boxShadow: 'none',
        width: dense ? 'auto' : fullWidth ? '100%' : 'fit-content',
        whiteSpace: 'nowrap',
        gap: '5px',
        '& .MuiButton-startIcon, & .MuiButton-endIcon': {
          marginLeft: 0,
          marginRight: 0,
        },
        ...variantStyles,
        '&.Mui-disabled': {
          opacity: 0.55,
          color: variantStyles.color,
          backgroundColor: variantStyles.backgroundColor,
        },
      }}
    >
      <>
        {isLoading ? (
          <>
            <CircularProgress
              size={14}
              sx={{ color: variantStyles.color || 'inherit' }}
            />
            <PaddingBox />
          </>
        ) : showText ? (
          <span style={{ fontWeight: 600 }}>{showText}</span>
        ) : (
          iconComponent
        )}
      </>
    </Button>
  );
}
